// --- React & Redux
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
// --- External UI Libraries
import { Col, Row } from 'antd';
import Button from '@dtci/spellbook/Button';
import Input from '@dtci/spellbook/Input';
import Select from '@dtci/spellbook/Select';
// --- Components
import { InputLabel } from 'datastage/components/InputLabel';
// --- Actions & Hooks
import { usePmxDeliveriesActions } from 'datastage/store/pmxDeliveries/usePmxDeliveries';
import { usePmxDeliveriesData } from '../../hooks/usePmxDeliveriesData';
// --- Utilities
import { getItemRange } from 'datastage/utils/pagination';
import { parseSearchParams, updateSearchParams } from 'datastage/utils/urlParams';
import { cleanPMXFilter, findActionByValue, findStatusByValue } from './utils/pmxDeliveriesSearchUtils';
import { validateSpecialCharacters } from 'datastage/utils/validateSpecialCharacters';
// --- Styles
import './styles.scss';

/**
 * PMX Deliveries Search Form
 *
 * Features:
 * - Renders a filter form for PMX deliveries (SKU, Action, Status)
 * - Debounced auto-search on input change (400ms delay)
 * - Input validation for SKU field
 * - Syncs filter state with URL params bidirectionally
 * - Reset button clears all filters
 *
 * @returns {JSX.Element}
 */
export function PMXDeliveriesSearchForm() {
    const location = useLocation();
    const navigate = useNavigate();
    const isInitialMount = useRef(true);
    const debounceTimerRef = useRef(null);

    const {
        fetchPmxDeliveries,
        updatePMXDeliveries,
        pmxDeliveriesRange,
        pmxDeliveriesSort,
        itemsPerPage,
    } = usePmxDeliveriesData();
    const { updatePMXDeliveriesSearchForm, clearPMXDeliveries } = usePmxDeliveriesActions();
    const { filterForm, actionOptions, statusOptions } = useSelector((s) => s.pmxDeliveries);

    // Validation state for inline error messages
    const [validationErrors, setValidationErrors] = useState({
        skuId: undefined,
    });

    // Sync URL params to form state on navigation (location.key changes on each navigation)
    useEffect(() => {
        const params = parseSearchParams(location.search);
        const filterData = buildFilterFromParams(params, actionOptions, statusOptions);

        // Handle pageSize from URL (TAP pattern)
        let rangeOverride;
        if (params.pageSize) {
            const newPageSize = parseInt(params.pageSize, 10);
            if (!isNaN(newPageSize) && newPageSize > 0) {
                rangeOverride = getItemRange(0, newPageSize);
                updatePMXDeliveries({
                    itemsPerPage: newPageSize,
                    pmxDeliveriesRange: rangeOverride,
                    currentPage: 0,
                });
            }
        }

        const hasUrlParams = Object.keys(filterData).length > 0;

        if (hasUrlParams) {
            updatePMXDeliveriesSearchForm({ key: 'filterForm', data: filterData });
        }

        // Merge URL-derived filters onto Redux filterForm so defaults apply when the URL
        // only carries partial params (e.g. pageSize).
        const filterForFetch = hasUrlParams ? { ...filterForm, ...filterData } : filterForm;

        fetchData(filterForFetch, rangeOverride);
        isInitialMount.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key]);

    // Re-parse URL params when options load (needed for select values that depend on options)
    useEffect(() => {
        if (isInitialMount.current) return;
        if (!Array.isArray(actionOptions) || actionOptions.length === 0) return;
        if (!Array.isArray(statusOptions) || statusOptions.length === 0) return;

        const params = parseSearchParams(location.search);
        if (params.action || params.status) {
            const filterData = buildFilterFromParams(params, actionOptions, statusOptions);
            const mergedFilterData = { ...filterForm, ...filterData };
            updatePMXDeliveriesSearchForm({ key: 'filterForm', data: filterData });

            // Handle pageSize if present
            let rangeOverride;
            if (params.pageSize) {
                const newPageSize = parseInt(params.pageSize, 10);
                if (!isNaN(newPageSize) && newPageSize > 0) {
                    rangeOverride = getItemRange(0, newPageSize);
                }
            }

            fetchData(mergedFilterData, rangeOverride);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actionOptions, statusOptions]);

    /**
     * Builds filter form data from URL search params.
     * Converts action/status values back to option objects for the Select components.
     */
    function buildFilterFromParams(params, actionOpts, statusOpts) {
        const filterData = {};

        if (params.skuId) filterData.skuId = params.skuId;

        if (params.action && Array.isArray(actionOpts) && actionOpts.length > 0) {
            const matched = findActionByValue(actionOpts, params.action);
            if (matched) filterData.action = matched;
        }

        if (params.status && Array.isArray(statusOpts) && statusOpts.length > 0) {
            const matched = findStatusByValue(statusOpts, params.status);
            if (matched) filterData.status = matched;
        }

        return filterData;
    }

    /**
     * Fetches PMX deliveries data and updates Redux state.
     * @param {Object} filterData - Filter criteria
     * @param {Array} rangeOverride - Optional range override (e.g., when pageSize changes)
     */
    const fetchData = async (filterData, rangeOverride) => {
        // Merge with current filterForm to include all fields
        const mergedFilter = { ...filterForm, ...filterData };
        const range = rangeOverride ?? getItemRange(0, itemsPerPage);
        const data = await fetchPmxDeliveries(range, mergedFilter);
        if (data) {
            updatePMXDeliveries({
                deliveries: data?.content ?? [],
                totalPages: data?.totalPages ?? 0,
                currentPage: data?.pageable?.pageNumber ?? 0,
                totalItems: data?.totalElements ?? 0,
                pmxDeliveriesRange: range,
            });
        }
    };

    /**
     * Reset navigates to clean URL; the location.key useEffect handles re-fetch.
     */
    const handleResetFilter = () => {
        clearPMXDeliveries();
        setValidationErrors({ skuId: undefined });
        navigate(location.pathname);
    };

    /**
     * Handles input changes with validation and debounced search.
     * Validates SKU for special characters.
     * Triggers search after 400ms of no typing.
     */
    function handleInputChange(field, value) {
        let newValidationErrors = validationErrors;

        // Validate SKU field for special characters
        if (field === 'skuId') {
            const error = validateSpecialCharacters(value);
            newValidationErrors = { ...validationErrors, [field]: error };
            setValidationErrors(newValidationErrors);
        }

        // Update form state
        const newFilterForm = { ...filterForm, [field]: value };
        updatePMXDeliveriesSearchForm({
            key: 'filterForm',
            data: { [field]: value },
        });

        // Debounce the search to avoid excessive API calls
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            const hasErrors = Object.values(newValidationErrors).some((error) => error);
            if (!hasErrors) {
                const firstPageRange = getItemRange(0, itemsPerPage);
                updatePMXDeliveries({ pmxDeliveriesRange: firstPageRange, currentPage: 0 });
                const filtered = cleanPMXFilter(
                    newFilterForm,
                    firstPageRange,
                    pmxDeliveriesSort,
                );
                updateSearchParams(location, navigate, filtered);
            }
        }, 400);
    }

    return (
        <Row className="deliveries-filter-form">
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Sku">
                    <Input
                        value={filterForm?.skuId || ''}
                        onChange={(e) => handleInputChange('skuId', e.target.value)}
                        width="auto"
                        id="skuId"
                        name="skuId"
                        placeholder="Enter"
                        className={validationErrors.skuId ? 'input-error' : ''}
                    />
                    {validationErrors.skuId && (
                        <div className="validation-error">{validationErrors.skuId}</div>
                    )}
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Action">
                    <Select
                        options={actionOptions}
                        isClearable
                        isSearchable
                        onChange={(value) => handleInputChange('action', value)}
                        value={filterForm?.action || ''}
                    />
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Status">
                    <Select
                        options={statusOptions}
                        isClearable
                        isSearchable
                        onChange={(value) => handleInputChange('status', value)}
                        value={filterForm?.status || ''}
                    />
                </InputLabel>
            </Col>
            <Col>
                <Button
                    className="reset-button"
                    sizeType="small"
                    styleType="standard"
                    onClick={handleResetFilter}
                >
                    Reset
                </Button>
            </Col>
        </Row>
    );
}
